import { Auth0Client } from '@auth0/auth0-spa-js';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { initFeatureFlagsForUser, trackCustomError } from '../utils/datadog';
import { PermissionString } from '../utils/permissions';

/**
 * Authentication as a bootstrap phase.
 *
 * The app awaits `whenAuthReady()` once, before first render; route guards,
 * the axios interceptors, and feature-flag initialization all read the
 * settled result, so nothing can race token availability.
 *
 * Nothing here runs at import time — the client and the bootstrap start
 * lazily — so importing this module outside a browser is side-effect free.
 */

interface TokenPayload extends JwtPayload {
  email?: string;
  permissions?: string[];
}

export type ResolvedAuth = {
  isAuthenticated: boolean;
  email?: string;
  permissions: string[];
  hasPermission: (permission: PermissionString) => boolean;
};

const UNAUTHENTICATED: ResolvedAuth = {
  isAuthenticated: false,
  permissions: [],
  hasPermission: () => false,
};

const domain = import.meta.env.VITE_AUTH0_DOMAIN || '';
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID || '';
const audience = import.meta.env.VITE_AUTH0_AUDIENCE || '';
const issuer = import.meta.env.VITE_AUTH0_ISSUER || `https://${domain}/`;

let auth0Client: Auth0Client | null = null;
let authReadyPromise: Promise<ResolvedAuth> | null = null;
let resolvedAuth: ResolvedAuth | null = null;
// Deduplicates concurrent login redirects (e.g. several 401s landing at once).
let redirectingToLogin = false;
let interceptorsInstalled = false;

function getAuth0Client(): Auth0Client {
  auth0Client ??= new Auth0Client({
    domain,
    clientId,
    issuer,
    useRefreshTokens: true,
    cacheLocation: 'localstorage',
    authorizationParams: {
      audience,
      redirect_uri: window.location.origin,
      screen_hint: 'login',
    },
  });
  return auth0Client;
}

async function bootstrapAuth(): Promise<ResolvedAuth> {
  const client = getAuth0Client();

  // Finish the login redirect if Auth0 sent us back with a code, and restore
  // the pre-login URL.
  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.has('code') && searchParams.has('state')) {
    try {
      const { appState } = await client.handleRedirectCallback();
      window.history.replaceState(
        {},
        document.title,
        appState?.returnTo ?? window.location.pathname
      );
    } catch (error) {
      console.error('Auth0 redirect callback failed:', error);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }

  try {
    const token = await client.getTokenSilently();
    const decoded = jwtDecode<TokenPayload>(token);
    const permissions = decoded.permissions ?? [];
    // Flags target on the user's email; start the provider with the user
    // context in place. Not awaited — readers gate on settlement themselves.
    void initFeatureFlagsForUser(decoded.email);
    return {
      isAuthenticated: true,
      email: decoded.email,
      permissions,
      hasPermission: (permission) => permissions.includes(permission),
    };
  } catch (error) {
    // No session, or a session that can no longer produce tokens (expired or
    // revoked refresh token). Either way the app treats it as signed out.
    console.warn('No usable Auth0 session:', error);
    return UNAUTHENTICATED;
  }
}

/**
 * Starts the one-shot auth bootstrap on first call and returns its promise.
 * Never rejects — a failed bootstrap resolves as signed out.
 */
export function whenAuthReady(): Promise<ResolvedAuth> {
  authReadyPromise ??= bootstrapAuth().then((resolved) => {
    resolvedAuth = resolved;
    return resolved;
  });
  return authReadyPromise;
}

/**
 * Synchronous snapshot of the bootstrapped auth state; signed out until the
 * bootstrap resolves.
 */
export function getResolvedAuth(): ResolvedAuth {
  return resolvedAuth ?? UNAUTHENTICATED;
}

/**
 * Starts the interactive login redirect; after login the app returns to
 * `returnTo` (default: the current URL).
 */
export async function loginWithRedirect(returnTo?: string): Promise<void> {
  if (redirectingToLogin) {
    return;
  }
  redirectingToLogin = true;
  try {
    await getAuth0Client().loginWithRedirect({
      appState: { returnTo: returnTo ?? window.location.href },
    });
  } catch (error) {
    console.error('loginWithRedirect failed:', error);
    redirectingToLogin = false;
  }
}

/**
 * Logs out of Auth0 and returns to `returnTo` (default: the app origin).
 */
export function logout(returnTo?: string): Promise<void> {
  return getAuth0Client().logout({
    logoutParams: { returnTo: returnTo ?? window.location.origin },
  });
}

/**
 * Resolves an access token for the current session, or null when signed out
 * or when acquisition fails.
 */
export async function getToken(): Promise<string | null> {
  const { isAuthenticated } = await whenAuthReady();
  if (!isAuthenticated) {
    return null;
  }
  try {
    return await getAuth0Client().getTokenSilently();
  } catch (error) {
    console.warn('Error fetching access token:', error);
    return null;
  }
}

const MAX_REPORTED_BODY_LENGTH = 500;
const GROUPED_PATH_SEGMENTS = 4;

/**
 * Installs the global axios interceptors (idempotent). Every request awaits
 * the auth bootstrap and carries a bearer token; a session that cannot
 * produce a token starts the interactive login instead.
 */
export function installAuthInterceptors(): void {
  if (interceptorsInstalled) {
    return;
  }
  interceptorsInstalled = true;

  axios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    await whenAuthReady();
    try {
      const token = await getAuth0Client().getTokenSilently();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      console.warn('No usable session; starting login:', error);
      await loginWithRedirect();
      // Keep the request pending while the redirect to login takes place.
      await new Promise(() => {});
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const status = error.response?.status;

      if (status && status >= 400) {
        const body = error.response?.data;
        const detail = (body as { detail?: unknown } | undefined)?.detail;

        const method = error.config?.method?.toUpperCase() ?? 'UNKNOWN';
        const url = error.config?.url?.split('?')[0] ?? 'unknown';
        const endpoint = url
          .split('/')
          .slice(0, GROUPED_PATH_SEGMENTS)
          .join('/');

        trackCustomError(
          new Error(
            `${status} ${method} ${endpoint}` +
              (typeof detail === 'string' ? `: ${detail}` : '')
          ),
          {
            status,
            method,
            url,
            responseBody:
              typeof body === 'string'
                ? body.slice(0, MAX_REPORTED_BODY_LENGTH)
                : body,
          }
        );
      }

      if (status === 401) {
        void loginWithRedirect();
      }

      return Promise.reject(error);
    }
  );
}
